import React, { JSX, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { cloneDeep } from "lodash";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import BenchmarkingServiceAreaFilters from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingServiceAreaFilters";
import NetworkFlyoutCountiesTable from "@app/overviews/network-benchmarking-dashboard/filters/NetworkFlyoutCountiesTable";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { UserContext } from "@app/user/UserContext";
import Roles from "@app/user/roles";
import { useMedicaidContractDetails } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/hooks/useMedicaidContractDetails";
import { MedicaidFiltersContext } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/MedicaidFiltersContext";
import { MedicaidNetwork } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/MedicaidNetwork";
import { NetworkProgram } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/NetworkProgram";

interface MedicaidNetworkFlyoutProps {
  contract: MedicaidNetwork;
}

const MedicaidNetworkFlyout = (props: MedicaidNetworkFlyoutProps): JSX.Element => {
  const [isYourNetwork, setIsYourNetwork] = useState<boolean>(false);

  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const medicaidFiltersContext = useContext(MedicaidFiltersContext);
  const userContext = useContext(UserContext);

  useEffect(() => {
    if (!!props.contract?.contractId) {
      setIsYourNetwork((props.contract?.parentOrganization === "Your QES Networks"));
    }
  }, [props.contract?.contractId]);

  const { contractDetails, isLoading: loading } = useMedicaidContractDetails(
    props.contract?.contractId,
    props.contract?.versionId,
    benchmarkingFiltersContext.displayedPrimaryFilters
  );

  const contract = useMemo(() => {
    if (!contractDetails) return null;

    return {
      ...cloneDeep(props.contract),
      counties: contractDetails.counties,
      plans: contractDetails.plans,
    };
  }, [contractDetails, props.contract]);

  const getProgramName = (): string => {
    const selectedProgram: NetworkProgram = medicaidFiltersContext.programs.find(x => x.programKey === benchmarkingFiltersContext.programKey);

    if (!!selectedProgram) {
      return selectedProgram.programName;
    }
  }

  return (
    <React.Fragment>
      <h3 className="lead">
        <b>Payer Name:</b> {props.contract.parentOrganization}
      </h3>
      <h3 className="lead mb-4">
        <b>Program Name:</b> {getProgramName()}
      </h3>

      <BenchmarkingServiceAreaFilters />

      <div className="row">
        <div className="col-12">
          {loading ? (
            <div className="text-center">
              <LoadingSpinner />
            </div>
          ) : (
            <AccordionContainer>
              <Accordion
                title={`${!isYourNetwork ? "Active " : ""}Counties`}
                testId="h-contract-flyout__counties-accordion"
              >
                <NetworkFlyoutCountiesTable
                  contract={contract}
                  data={contract?.counties}
                  isYourNetwork={isYourNetwork}
                  loading={loading}
                  onApplyServiceAreaClick={medicaidFiltersContext.onApplyServiceAreaClick}
                />
              </Accordion>

              {(userContext.roles.includes(Roles.CLIENT_ADMIN) || isYourNetwork) && (
                <Accordion
                  title="Data Details"
                  testId="h-contract-flyout__data-details"
                >
                  <div className="row">
                    <div className="col-md-12">
                      <label className="label-m">
                        QES Data
                      </label>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-3">
                      <label className="font-weight-bold">
                        QES Project Id:
                      </label>
                    </div>
                    <div className="col-md-9">
                      {!!props.contract?.projectId ?
                        <Link to={`/projects/${props.contract.projectId}`} target="_blank" rel="noopener noreferrer">{props.contract.projectId}</Link> :
                        "---"
                      }
                    </div>
                  </div>
                </Accordion>
              )}
            </AccordionContainer>
          )}
        </div>
      </div>
    </React.Fragment>
  );
}

export default MedicaidNetworkFlyout;
